import React, { useEffect, useRef, useState } from 'react'
import Navbar from './Navbar'
import Leader from './Form/Leader'
import Group from './Form/Group'
import axios from 'axios'
import { toast, ToastContainer } from 'react-toastify'
import TextInput from './Form/elements/TextInput'
import { isEmptyField, validateEmail, validatePhone } from '../utils'
import Loading from './Loading'

const MINIMUM_GUEST_REQUIRED = 2

export default function Home() {
	const inputRef = useRef(null)
	const [loading, setLoading] = useState(false)

	const [editingGuestCount, setEditingGuestCount] = useState(true)

	const [updatingGuestData, setUpdatingGuestData] = useState(false)

	const [guestCount, setGuestCount] = useState(MINIMUM_GUEST_REQUIRED)
	const [error, setError] = useState('')

	const [guestData, setGuestData] = useState([
		{
			name: '',
			gender: '',
			contact: '',
			email: '',
		},
		{
			name: '',
			gender: '',
			contact: '',
			email: '',
		},
	])

	const [otherData, setOtherData] = useState({
		travelAgentName: '',
		message: '',
	})

	const validateFormData = () => {
		// Calculate required number of filled entries (50% of total)
		const requiredFilledEntries = Math.ceil(guestCount / 2)
		let filledEntriesCount = 0

		const emailsSet = new Set()
		const contactsSet = new Set()

		for (let i = 0; i < guestData.length; i++) {
			const guest = guestData[i]

			// Check if this entry is completely filled
			const isEntryFilled = guest.name && guest.gender && guest.contact
			if (isEntryFilled) {
				filledEntriesCount++
			}

			// Validate filled entries
			if (guest.name || guest.gender || guest.contact || guest.email) {
				// If any field is filled, all required fields must be filled
				if (isEmptyField(guest.name) || isEmptyField(guest.gender) || isEmptyField(guest.contact)) {
					toast.error(`Please fill all required fields for Guest ${i + 1}.`)
					return false
				}

				// Contact validation
				if (guest.contact) {
					if (!validatePhone(guest.contact)) {
						toast.error(`Invalid Contact for Guest ${i + 1}.`)
						return false
					}
					if (contactsSet.has(guest.contact.trim())) {
						toast.error(`Duplicate contact number found for Guest ${i + 1}.`)
						return false
					}
					contactsSet.add(guest.contact.trim())
				}

				// Email validation (if provided)
				if (guest.email) {
					if (!validateEmail(guest.email)) {
						toast.error(`Invalid Email for Guest ${i + 1}.`)
						return false
					}
					if (emailsSet.has(guest.email.trim())) {
						toast.error(`Duplicate email found for Guest ${i + 1}.`)
						return false
					}
					emailsSet.add(guest.email.trim())
				}
			}
		}

		// Check if minimum required entries are filled
		if (filledEntriesCount < requiredFilledEntries) {
			toast.error(
				`Please fill details for at least ${requiredFilledEntries} guests (50% of total ${guestCount} guests).`,
			)
			return false
		}

		return true
	}

	const resetForm = () => {
		setGuestData([
			{
				name: '',
				gender: '',
				contact: '',
				email: '',
			},
			{
				name: '',
				gender: '',
				contact: '',
				email: '',
			},
		])
		setOtherData({
			travelAgentName: '',
			message: '',
		})
		setEditingGuestCount(true)
		setGuestCount(MINIMUM_GUEST_REQUIRED)
	}

	const handleSubmit = async (e) => {
		e.preventDefault()

		if (!validateFormData()) return

		// toast.error('Resolve errors to continue!')

		setLoading(true)

		let filledGuests = []
		for (let i = 0; i < guestData.length; i++) {
			const guest = guestData[i]

			// Check if this entry is completely filled
			const isEntryFilled = guest.name && guest.gender && guest.contact
			if (isEntryFilled) {
				filledGuests.push(guest)
			}
		}

		const otherGuests = filledGuests.slice(1)

		const dataToSend = {
			leader_name: filledGuests[0].name,
			leader_gender: filledGuests[0].gender,
			leader_contact: filledGuests[0].contact,
			leader_email: filledGuests[0].email,
			guests_count: guestData.length,
			message: otherData.message,
			travelAgent: otherData.travelAgentName,
			guests: otherGuests,
		}

		try {
			const response = await axios.post(`/api/groups/`, dataToSend, {
				withCredentials: true,
			})

			if (response.status === 201) {
				setLoading(false)
				toast.success('Details successfully submitted!')
				resetForm()
			}
		} catch (error) {
			setLoading(false)
			toast.error('Something went wrong!')
			console.error('Error submitting the form: ', error)
		}
	}

	const handleGuestCountChange = (e) => {
		// Remove any non-digit characters
		const numericValue = e.target.value.replace(/[^0-9]/g, '')

		const numberValue = numericValue === '' ? 0 : parseInt(numericValue, 10)

		let min = MINIMUM_GUEST_REQUIRED,
			max = 50

		if (numberValue < min) {
			setError(`Value must be at least ${min}`)
		} else if (numberValue > max) {
			setError(`Value must be less than or equal to ${max}`)
		} else {
			setError('')
		}

		setGuestCount(parseInt(numericValue))
	}

	const changeToEditMode = () => {
		inputRef.current.disabled = false
		inputRef.current.focus()
		inputRef.current.select()
		setEditingGuestCount(true)
	}

	const handleGuestListUpdate = () => {
		if (guestCount < MINIMUM_GUEST_REQUIRED) return setError('Minimum 2 guest required!')

		inputRef.current.disabled = true
		setEditingGuestCount(false)

		if (guestData.length === guestCount) return setUpdatingGuestData(false)

		const data = []
		for (let i = 0; i < guestCount; i++) {
			data.push({
				name: '',
				gender: '',
				contact: '',
				email: '',
			})
		}

		setUpdatingGuestData(true)
		setTimeout(() => {
			setGuestData(data)
			setUpdatingGuestData(false)
		}, 800)
	}

	return (
		<>
			{loading && <Loading />}

			<div className=" w-full md:max-w-screen-md bg-[#f6f6fd] mx-auto drop-shadow-xl">
				<header className=" flex flex-col justify-start items-start space-y-3 px-6 pt-6 pb-4 bg-primary  text-white rounded-b-2xl md:rounded-none">
					<img
						src="https://d1crnb901o7gp8.cloudfront.net/static/img/logo.svg"
						alt=""
						className="h-12 rounded"
					/>

					<h1 className="  text-xl font-bold text-right">Hello Traveller !</h1>
					<p className=" ">
						Please fill in the details of Group Leader and Passengers for your upcoming trip. This
						helps us ensure a smooth and hassle-free experience.
					</p>
				</header>

				<main className="w-full p-3 sm:p-6 space-y-3 mt-4">
					{/* Group Details */}
					<section>
						<>
							<div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center">
								<h3 className=" font-bold text-black/90 text-2xl ">Group Details</h3>

								<div className="flex items-center gap-4 py-2 px-3 sm:px-5 rounded-md bg-primary/5 my-2 w-max">
									<p className="sm:text-lg font-medium !leading-5">Total Guest Count:</p>
									<input
										className="text-2xl sm:text-4xl bg-white/50 py-1 text-center rounded-md w-14 sm:w-20 disabled:cursor-not-allowed"
										value={guestCount}
										ref={inputRef}
										disabled={!editingGuestCount}
										onChange={handleGuestCountChange}
										autoFocus
										onFocus={(e) => e.currentTarget.select()}
										onKeyDown={(e) => {
											if (e.key === 'Enter') handleGuestListUpdate()
										}}
									/>

									<button
										onClick={editingGuestCount ? handleGuestListUpdate : changeToEditMode}
										disabled={error}
										className="text-sm sm:text-base flex items-center gap-1.5 bg-primary py-1 pl-2.5 pr-3 rounded-md text-white disabled:opacity-85 disabled:cursor-not-allowed transition-all duration-150 hover:scale-105 hover:disabled:scale-100 active:scale-95"
									>
										{editingGuestCount ? (
											<svg
												className="w-5 h-5"
												width="64px"
												height="64px"
												viewBox="0 0 24 24"
												fill="none"
												xmlns="http://www.w3.org/2000/svg"
											>
												<path
													d="M4.89163 13.2687L9.16582 17.5427L18.7085 8"
													stroke="#eee"
													strokeWidth="2.5"
													strokeLinecap="round"
													strokeLinejoin="round"
												/>
											</svg>
										) : (
											<svg
												className="w-5 h-5"
												width="64px"
												height="64px"
												viewBox="0 0 24 24"
												fill="none"
												xmlns="http://www.w3.org/2000/svg"
											>
												<path
													d="M21.2799 6.40005L11.7399 15.94C10.7899 16.89 7.96987 17.33 7.33987 16.7C6.70987 16.07 7.13987 13.25 8.08987 12.3L17.6399 2.75002C17.8754 2.49308 18.1605 2.28654 18.4781 2.14284C18.7956 1.99914 19.139 1.92124 19.4875 1.9139C19.8359 1.90657 20.1823 1.96991 20.5056 2.10012C20.8289 2.23033 21.1225 2.42473 21.3686 2.67153C21.6147 2.91833 21.8083 3.21243 21.9376 3.53609C22.0669 3.85976 22.1294 4.20626 22.1211 4.55471C22.1128 4.90316 22.0339 5.24635 21.8894 5.5635C21.7448 5.88065 21.5375 6.16524 21.2799 6.40005V6.40005Z"
													stroke="#eee"
													strokeWidth="1.5"
													strokeLinecap="round"
													strokeLinejoin="round"
												/>
												<path
													d="M11 4H6C4.93913 4 3.92178 4.42142 3.17163 5.17157C2.42149 5.92172 2 6.93913 2 8V18C2 19.0609 2.42149 20.0783 3.17163 20.8284C3.92178 21.5786 4.93913 22 6 22H17C19.21 22 20 20.2 20 18V13"
													stroke="#eee"
													strokeWidth="1.5"
													strokeLinecap="round"
													strokeLinejoin="round"
												/>
											</svg>
										)}
										<span>{editingGuestCount ? 'Update' : 'Edit'}</span>
									</button>

									{error && <p className="mt-1 text-sm text-red-500">{error}</p>}
								</div>
							</div>

							<div className="relative">
								<Group guestData={guestData} setGuestData={setGuestData} />
								{updatingGuestData && (
									<div className="absolute top-0 left-0 z-10 w-full h-full bg-white/10 backdrop-blur-sm rounded-md grid place-items-center">
										<img
											src="https://d1crnb901o7gp8.cloudfront.net/static-group/images+/loader.png"
											alt=""
											className=" animate-spin w-12 h-12"
										/>
									</div>
								)}
							</div>
						</>
					</section>

					{/* Separator */}
					<div className="px-5">
						<span className="bg-primary/10 w-full inline-block h-[1px]"></span>
					</div>

					{/* Other Details */}
					<section>
						<h3 className=" font-bold text-black text-xl ">Other Details</h3>
						<div className="bg-white p-5 flex flex-col mt-3 w-full shadow rounded-xl">
							<TextInput
								label={'Travel Agent (optional)'}
								name={'travelAgent'}
								placeholder={'Travel Agent'}
								type={'text'}
								value={otherData.travelAgentName}
								onChange={(e) =>
									setOtherData((prev) => {
										return { ...prev, travelAgentName: e.target.value }
									})
								}
							/>
							<div className="relative mt-4 w-full text-[#666666] space-y-1">
								<label className=" text-[#666666] text-[16px] mt-3">
									Any message for us (optional)
								</label>
								<textarea
									rows={4}
									type={'text'}
									placeholder={'Please let us know'}
									className="w-full pl-2  pr-3 py-2 appearance-none bg-transparent outline-none border-[2px] text-start border-[#CCCCCC] focus:border-primary shadow-sm rounded-md"
									value={otherData.message}
									onChange={(e) =>
										setOtherData((prev) => {
											return { ...prev, message: e.target.value }
										})
									}
								/>
							</div>
						</div>
					</section>

					{/* Submit Button */}
					<button
						onClick={handleSubmit}
						className="!mt-6  px-10 py-2 bg-primary rounded-lg text-lg  text-white"
					>
						Submit
					</button>
				</main>
			</div>
		</>
	)
}
